import React, { useState } from 'react';
import { SaibaMais } from './SaibaMais';
import { Camera, Shield, Wifi, CheckCircle2, Phone, ArrowRight, Sparkles, Monitor, Zap, Eye } from 'lucide-react';
import { Button } from './ui/button';

const PRODUCTS = [
  {
    id: 'cameras',
    title: 'Câmeras de Segurança CFTV',
    subtitle: 'Vigilância 24h',
    description: 'Câmeras dome, bullet e speed dome com resolução Full HD e 4K. Gravação noturna, detecção de movimento inteligente e acesso remoto pelo celular, de qualquer lugar.',
    icon: Camera,
    features: [
      'Câmeras dome, bullet e speed dome Full HD / 4K',
      'Visão noturna infravermelha',
      'Detecção inteligente de movimento',
      'Acesso remoto pelo smartphone (iSIC / iMDVR)',
      'Gravação em nuvem e gravador local (DVR/NVR)',
    ],
    image: './intelbras-cameras.png',
  },
  {
    id: 'alarmes',
    title: 'Sistemas de Alarme',
    subtitle: 'Proteção Total',
    description: 'Centrais de alarme com sensores de presença passivos, magnéticos e acústicos. Integração com monitoramento profissional e notificação automática no celular.',
    icon: Shield,
    features: [
      'Centrais de alarme Intelbras AMT e ANM',
      'Sensores de presença PIR e dupla tecnologia',
      'Sensores magnéticos para portas e janelas',
      'Sirene interna/externa com bateria de backup',
      'Notificação por app e monitoramento 24h',
    ],
    image: './intelbras-alarme.png',
  },
  {
    id: 'wifi',
    title: 'Redes Wi-Fi Intelbras',
    subtitle: 'Conectividade Corporativa',
    description: 'Access points e roteadores profissionais para cobertura total do seu espaço. Alta velocidade, múltiplos usuários simultâneos e gerenciamento centralizado em nuvem.',
    icon: Wifi,
    features: [
      'Access points Intelbras AP para ambientes corporativos',
      'Roteadores Wi-Fi 6 de alta performance',
      'Cobertura de grandes áreas com múltiplos APs',
      'Portal captive para controle de acesso de clientes',
      'Gestão centralizada via plataforma em nuvem',
    ],
    image: './intelbras-wifi.png',
  },
];

const Intelbras = ({ showLearnMore }) => {
  const [activeTab, setActiveTab] = useState('cameras');

  const whatsappUrl = 'https://wa.me/5516991378312?text=' + encodeURIComponent('Olá! Gostaria de um orçamento para câmeras de segurança, alarme ou Wi-Fi Intelbras.');

  const active = PRODUCTS.find(p => p.id === activeTab);
  const ActiveIcon = active.icon;

  return (
    <section id="intelbras" className="py-20 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden" style={{ scrollMarginTop: '100px' }}>
      {/* BG effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-green-600/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-emerald-600/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
            <Sparkles className="w-4 h-4 text-green-400" />
            <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">Parceiro Autorizado Intelbras</span>
          </div>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-green-500/50" />
            <span className="text-green-400 font-bold text-xl tracking-widest uppercase">Intelbras</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-green-500/50" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Segurança e{' '}
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Conectividade</span>
            <br />
            para sua Empresa
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Instalação e configuração profissional de câmeras CFTV, sistemas de alarme e redes Wi-Fi com equipamentos Intelbras — a marca líder em segurança eletrônica do Brasil.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-10">
          <div className="bg-slate-800/70 border border-slate-700/50 p-1.5 rounded-2xl flex gap-1.5">
            {PRODUCTS.map((p) => {
              const Icon = p.icon;
              const isActive = activeTab === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveTab(p.id)}
                  data-testid={`intelbras-tab-${p.id}`}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    isActive
                      ? 'text-white shadow-lg shadow-green-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                  style={isActive ? { background: 'linear-gradient(135deg, #00A651, #00c461)' } : {}}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {p.id === 'cameras' ? 'Câmeras CFTV' : p.id === 'alarmes' ? 'Alarmes' : 'Wi-Fi'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Panel */}
        <div className="max-w-5xl mx-auto">
          <div
            className="bg-slate-900/70 border border-green-500/25 rounded-3xl overflow-hidden backdrop-blur-sm transition-all duration-300"
            data-testid="intelbras-panel"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left: Image */}
              <div className="relative overflow-hidden min-h-[280px] lg:min-h-[420px]">
                <img
                  src={active.image}
                  alt={active.title}
                  className="w-full h-full object-cover object-center"
                  style={{ minHeight: '280px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/30 via-transparent to-transparent lg:from-transparent lg:to-slate-900/70" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/80 lg:to-slate-900/30" />
                {/* Product badge — verde Intelbras */}
                <div className="absolute top-4 left-4 z-10">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #00A651, #00c461)' }}
                  >
                    <ActiveIcon className="w-3.5 h-3.5" />
                    {active.subtitle}
                  </span>
                </div>
              </div>

              {/* Right: Info */}
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                {/* Icon box — verde Intelbras */}
                <div
                  className="inline-flex w-14 h-14 items-center justify-center rounded-2xl mb-5 shadow-lg shadow-green-500/20"
                  style={{ background: 'linear-gradient(135deg, #00A651, #00c461)' }}
                >
                  <ActiveIcon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{active.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">{active.description}</p>
                <ul className="space-y-2.5 mb-8">
                  {active.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {/* CTA button — verde Intelbras */}
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="intelbras-whatsapp-cta">
                  <Button
                    className="w-full text-white font-bold py-4 text-sm rounded-xl shadow-lg shadow-green-500/25 transition-all hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #00A651, #00c461)' }}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Solicitar Orçamento no WhatsApp
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto">
          {[
            { icon: Zap, label: 'Instalação Rápida', desc: 'Agendamento ágil', color: 'text-yellow-400' },
            { icon: Shield, label: 'Garantia de Fábrica', desc: 'Produtos originais Intelbras', color: 'text-green-400' },
            { icon: Eye, label: 'Suporte Pós-Instalação', desc: 'Assistência técnica inclusa', color: 'text-blue-400' },
            { icon: Monitor, label: 'Acesso Remoto', desc: 'Monitore pelo celular', color: 'text-orange-400' },
          ].map((badge, i) => (
            <div key={i} className="bg-slate-800/50 border border-slate-700/40 rounded-2xl p-4 text-center hover:border-slate-600 transition-colors">
              <badge.icon className={`w-6 h-6 ${badge.color} mx-auto mb-2`} />
              <p className="text-white text-sm font-semibold">{badge.label}</p>
              <p className="text-slate-500 text-xs mt-0.5">{badge.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-400 text-sm mb-4">
            Atendemos empresas e residências em Ribeirão Preto e região
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="/orcamento-intelbras" data-testid="intelbras-orcamento-btn">
              <Button
                size="lg"
                className="text-white font-bold px-10 shadow-xl shadow-[#00A651]/25 transition-all hover:scale-105 hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #00A651, #00c461)' }}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Fazer Orçamento Online
              </Button>
            </a>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="intelbras-bottom-cta">
              <Button
                size="lg"
                variant="outline"
                className="border-[#00A651]/50 text-[#00A651] hover:bg-[#00A651]/10 font-bold px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Falar com Especialista
              </Button>
            </a>
          </div>
        </div>
      </div>
      {showLearnMore && <SaibaMais to="/intelbras" label="Saiba mais sobre Câmeras Intelbras" />}
    </section>
  );
};

export default Intelbras;
